import React, { useState, useEffect } from 'react';
import { auth, db } from '../../config/firebase';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { testStoryStatusCheck } from '../../utils/testStoryService';

/**
 * Debug function to list recent story progress documents for the current user
 */
const debugStoryProgress = async () => {
  try {
    console.log('🔍 DEBUG: Looking for recent story progress documents...');
    
    if (!auth.currentUser) {
      console.error('❌ DEBUG: No authenticated user found');
      return null;
    }
    
    const q = query(
      collection(db, 'storyProgress'),
      where('userId', '==', auth.currentUser.uid),
      orderBy('createdAt', 'desc'),
      limit(5)
    );
    
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      console.error('❌ DEBUG: No story progress documents found');
      return null;
    }
    
    const results = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      results.push({
        id: doc.id,
        status: data.status,
        hasResult: !!data.result,
        createdAt: data.createdAt?.toDate?.().toISOString() || 'unknown',
        bookId: data.bookId || 'unknown'
      });
    });
    
    console.table(results);
    console.log('To check a specific document, call: debugStoryDocument("document-id")');
    return results;
  } catch (error) {
    console.error('❌ DEBUG ERROR:', error);
    return null;
  }
};

/**
 * Debug function to check a specific story progress document
 */
const debugStoryDocument = async (documentId) => {
  if (!documentId) {
    console.error('❌ Please provide a document ID');
    return null;
  }
  
  try {
    console.log(`🔍 Checking document ${documentId}...`);
    const result = await testStoryStatusCheck(documentId);
    console.log('📄 Document check result:', result);
    return result;
  } catch (error) {
    console.error('❌ Error fetching document:', error);
    return null;
  }
};

// Make the debug functions available globally
// @ts-ignore - Add to window
window.debugStoryProgress = debugStoryProgress;
// @ts-ignore - Add to window
window.debugStoryDocument = debugStoryDocument;

/**
 * StoryDebugTools component - Provides UI for debugging story loading issues
 */
export default function StoryDebugTools() {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);

  // Initialize debug tools and announce their availability on component mount
  useEffect(() => {
    console.log('🔍 DEBUG TOOLS READY - Use these browser console commands:');
    console.log('  • debugStoryProgress() - List recent story progress documents');
    console.log('  • debugStoryDocument("id") - Inspect a specific document');
  }, []);
  
  // Run the debug function to check recent story progress
  const runDebug = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await debugStoryProgress();
      setResults(data || []);
      if (!data || data.length === 0) {
        setError('No story progress documents found');
      }
    } catch (err) {
      console.error('Debug failed:', err);
      setError(err.message || 'Failed to retrieve story progress');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Check a specific document
  const checkDocument = async (id) => {
    try {
      await debugStoryDocument(id);
    } catch (err) {
      console.error('Document check failed:', err);
    }
  };

  return (
    <div className="fixed top-4 right-4 z-[9999]">
      {!isOpen ? (
        <button 
          onClick={() => setIsOpen(true)}
          className="bg-red-600 hover:bg-red-700 text-white py-3 px-6 rounded-md font-bold shadow-lg flex items-center"
        >
          <span className="mr-2">🔍</span> Debug Story Loading
        </button>
      ) : (
        <div className="bg-white border-2 border-red-500 rounded-lg shadow-lg p-4 w-96">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold">Story Debug Tools</h3>
            <button 
              onClick={() => setIsOpen(false)}
              className="bg-gray-200 hover:bg-gray-300 rounded-full w-6 h-6 flex items-center justify-center"
            >
              ×
            </button>
          </div>
          
          <button
            onClick={runDebug}
            disabled={isLoading}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded mb-4 disabled:bg-blue-300"
          >
            {isLoading ? 'Checking...' : 'Check Recent Story Progress'}
          </button>
          
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded mb-4 text-sm">
              {error}
            </div>
          )}
          
          {results.length > 0 && (
            <div className="mb-4 max-h-40 overflow-auto border border-gray-200 rounded">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="p-2 text-left">ID</th>
                    <th className="p-2 text-left">Status</th>
                    <th className="p-2 text-left">Has Result</th>
                    <th className="p-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((item) => (
                    <tr key={item.id} className="border-t hover:bg-gray-50">
                      <td className="p-2">{item.id.substring(0, 6)}...</td>
                      <td className="p-2">
                        <span className={`px-2 py-1 rounded text-xs ${
                          item.status === 'completed' ? 'bg-green-100 text-green-800' :
                          item.status === 'failed' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="p-2">{item.hasResult ? '✅' : '❌'}</td>
                      <td className="p-2">
                        <button 
                          onClick={() => checkDocument(item.id)}
                          className="text-blue-500 hover:underline"
                        >
                          Inspect
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          <div className="text-xs text-gray-500 border-t pt-2">
            <p className="mb-1">Browser console commands:</p>
            <code className="bg-gray-100 px-1 rounded">debugStoryProgress()</code> - List documents<br />
            <code className="bg-gray-100 px-1 rounded">debugStoryDocument("id")</code> - Inspect document
          </div>
        </div>
      )}
    </div>
  );
}
